<mat-card class="detail-card" valumizeCardContextMenu [commentSlug]="'deal-return-summary'">
    <mat-card-header class="detail-card-header">
        <mat-card-title data-test="title">Return Summary</mat-card-title>
    </mat-card-header>
    <mat-card-content class="detail-card-content">
        <form [formGroup]="dealReturnSummaryForm">
            <div class="key-value-pair-section">
                <div class="key-value-pair-column">
                    <div class="key-value-pair">
                        <div class="key-value-item">Bid Price Adjustment</div>
                        <div class="key-value-item">
                            @if (readOnly || !isEditable) {
                                {{ deal?.bidPriceAdjustment | traceableFormat }}
                            } @else {
                                <input matInput class="input-number-narrow" formControlName="bidPriceAdjustment" type="number" (blur)="onBidPriceAdjustmentChange()"/>
                            }
                        </div>
                    </div>
                    <div class="key-value-pair">
                        <div class="key-value-item">Discount to NAV</div>
                        <div class="key-value-item"><span class="discount-to-nav-value">{{ discountToNAV | traceableFormat }}</span></div>
                    </div>
                </div>
            </div>
        </form>
        @if ((discountToNAV?.fraction ?? 0) > 1) {
            <valumize-info-panel [appearance]="'WARNING'"
                                 [message]="'Discount of ' + (discountToNAV | traceableFormat) + ' exceeds 100% threshold, leading to a bid price of ' + (bidPrice | traceableFormat) + '. Please ensure your cutoffs are correct.'"></valumize-info-panel>
        }
        @if (previewError$ | async; as previewError) {
            <valumize-info-panel [appearance]="'ERROR'" [message]="'Preview calculation failed: ' + previewError"></valumize-info-panel>
        }
        <section class="table-container">
            @if (returnSummary$ | async; as returnSummary) {
                <table mat-table [dataSource]="returnSummary.tableDatasource">
                    <!-- first header row (column groups) -->
                    <ng-container matColumnDef="groupsCase" sticky>
                        <th mat-header-cell *matHeaderCellDef
                            class="mat-mdc-header-cell, additional-header"
                            [attr.colspan]="1"></th>
                    </ng-container>
                    <ng-container matColumnDef="groupsProbability">
                        <th mat-header-cell *matHeaderCellDef
                            class="mat-mdc-header-cell, additional-header"
                            [attr.colspan]="1"></th>
                    </ng-container>
                    <ng-container matColumnDef="groupsNetPerformance">
                        <th mat-header-cell *matHeaderCellDef
                            class="mat-mdc-header-cell, additional-header"
                            [attr.colspan]="2">Net Performance
                        </th>
                    </ng-container>
                    <ng-container matColumnDef="groupsGrossPerformance">
                        <th mat-header-cell *matHeaderCellDef
                            class="mat-mdc-header-cell, additional-header"
                            [attr.colspan]="2">Gross Performance
                        </th>
                    </ng-container>
                    <ng-container matColumnDef="groupsNav">
                        <th mat-header-cell *matHeaderCellDef
                            class="mat-mdc-header-cell, additional-header"
                            [attr.colspan]="1">NAV
                        </th>
                    </ng-container>
                    <!-- columns and cells -->
                    <ng-container matColumnDef="case" sticky>
                        <th mat-header-cell *matHeaderCellDef></th>
                        <td mat-cell *matCellDef="let element">{{ element.scenario.code | codeFormat: codeTableScenarioType | async }}</td>
                        <td mat-footer-cell *matFooterCellDef>CF weighted case</td>
                    </ng-container>
                    <ng-container matColumnDef="probability">
                        <th mat-header-cell *matHeaderCellDef>
                            Probability
                            @if (!returnSummary.effectiveWeighting && returnSummary.weightingDivergences.length) {
                                <valumize-weighting-divergence-popover
                                    [divergences]="returnSummary.weightingDivergences"
                                    [userOverridable]="returnSummary.weightingUserOverridable">
                                </valumize-weighting-divergence-popover>
                            }
                        </th>
                        <td mat-cell *matCellDef="let element">
                            @if (returnSummary.effectiveWeighting) {
                                {{ probabilityLabel(element.scenario.code, returnSummary.effectiveWeighting) }}
                            } @else {
                                various
                            }
                        </td>
                        <td mat-footer-cell *matFooterCellDef>{{ returnSummary.cashflowWeightedCase.probability | traceableFormat }}</td>
                    </ng-container>
                    <ng-container matColumnDef="netPerformanceTvpi">
                        <th mat-header-cell *matHeaderCellDef>TVPI</th>
                        <td mat-cell *matCellDef="let element">{{ element.netPerformanceTvpi | traceableFormat }}</td>
                        <td mat-footer-cell *matFooterCellDef>{{ returnSummary.cashflowWeightedCase.netPerformanceTvpi | traceableFormat }}</td>
                    </ng-container>
                    <ng-container matColumnDef="netPerformanceIrr">
                        <th mat-header-cell *matHeaderCellDef>IRR</th>
                        <td mat-cell *matCellDef="let element">{{ element.netPerformanceIrr | traceableFormat }}</td>
                        <td mat-footer-cell *matFooterCellDef>{{ returnSummary.cashflowWeightedCase.netPerformanceIrr | traceableFormat }}</td>
                    </ng-container>
                    <ng-container matColumnDef="grossPerformanceTvpi">
                        <th mat-header-cell *matHeaderCellDef>TVPI</th>
                        <td mat-cell *matCellDef="let element">{{ element.grossPerformanceTvpi | traceableFormat }}</td>
                        <td mat-footer-cell *matFooterCellDef>{{ returnSummary.cashflowWeightedCase.grossPerformanceTvpi | traceableFormat }}</td>
                    </ng-container>
                    <ng-container matColumnDef="grossPerformanceIrr">
                        <th mat-header-cell *matHeaderCellDef>IRR</th>
                        <td mat-cell *matCellDef="let element">{{ element.grossPerformanceIrr | traceableFormat }}</td>
                        <td mat-footer-cell *matFooterCellDef>{{ returnSummary.cashflowWeightedCase.grossPerformanceIrr | traceableFormat }}</td>
                    </ng-container>
                    <ng-container matColumnDef="navUplift">
                        <th mat-header-cell *matHeaderCellDef>Uplift</th>
                        <td mat-cell *matCellDef="let element">{{ element.navUplift | traceableFormat }}</td>
                        <td mat-footer-cell *matFooterCellDef>{{ returnSummary.cashflowWeightedCase.navUplift | traceableFormat }}</td>
                    </ng-container>
                    <tr mat-header-row
                        *matHeaderRowDef="['groupsCase', 'groupsProbability', 'groupsNetPerformance', 'groupsGrossPerformance', 'groupsNav']"></tr>
                    <tr mat-header-row *matHeaderRowDef="returnSummary.tableColumns"></tr>
                    <tr mat-row *matRowDef="let row; columns: returnSummary.tableColumns"></tr>
                    <tr mat-footer-row *matFooterRowDef="returnSummary.tableColumns" class="row-important row-top-border"></tr>
                </table>
            }
        </section>
    </mat-card-content>
    @if (!readOnly) {
        <mat-card-actions class="detail-card-actions">
            @if (!isEditable) {
                <button (click)="openSensitivityPage()" [disabled]="(isEditDisabled$ | async)!" color="primary" data-test="sensitivity-button" mat-icon-button>
                    <mat-icon>troubleshoot</mat-icon>
                </button>
            }
            @if (isEditable) {
                <button (click)="openGoalSeekDialog()" color="primary" data-test="goal-seek-button" mat-icon-button>
                    <mat-icon>gps_fixed</mat-icon>
                </button>
            }
            @if (!isEditable) {
                <button (click)="editMode()" [disabled]="(isEditDisabled$ | async)!" color="primary" data-test="edit-button" mat-icon-button>
                    <mat-icon>edit</mat-icon>
                </button>
            }
            @if (isEditable) {
                <button (click)="save()" color="primary" data-test="save-button" mat-icon-button>
                    <mat-icon>save</mat-icon>
                </button>
            }
            @if (isEditable) {
                <button (click)="cancel()" color="primary" data-test="cancel-button" mat-icon-button>
                    <mat-icon>cancel</mat-icon>
                </button>
            }
            <valumize-card-comments-button [commentSlug]="'deal-return-summary'"></valumize-card-comments-button>
        </mat-card-actions>
    }
</mat-card>
